import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 5000, options = {}) => {
    const key = options.key;
    if (key && toastsRef.current.some((toast) => toast.key === key)) {
      return null;
    }

    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => {
      if (key && prev.some((toast) => toast.key === key)) {
        return prev;
      }
      return [...prev, {
        id,
        message,
        type,
        key,
        detail: options.detail || "",
        link: options.link || "",
        linkLabel: options.linkLabel || "Learn more",
      }];
    });

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const success = useCallback((message, duration, options) => {
    return addToast(message, 'success', duration, options);
  }, [addToast]);

  const error = useCallback((message, duration, options) => {
    return addToast(message, 'error', duration !== undefined ? duration : 0, options);
  }, [addToast]);

  const info = useCallback((message, duration, options) => {
    return addToast(message, 'info', duration, options);
  }, [addToast]);

  const warning = useCallback((message, duration, options) => {
    return addToast(message, 'warning', duration, options);
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, warning }}>
      {children}
    </ToastContext.Provider>
  );
};
